// noinspection JSUnusedGlobalSymbols

import { nextTick } from 'vue'
import { defineStore } from 'pinia'
import { useLocalStorage } from '@vueuse/core'
import router from '@/router/router'
import { useTwoFactorVerificationStore } from '@/stores/twoFactorVerificationStore'
import { useJwt } from '@vueuse/integrations/useJwt'
import { LoginResultStatus } from '@/api/resources/LoginResultResource.js'
import { useSignupStore } from '@/stores/signupStore.js'
import { HttpStatusCode } from '@/api/ApiGateway.js'
import { useOrganizationStore } from '@/stores/organizationStore.js'
import { Banner } from '@/models/utils/Toaster.js'
import { Format } from '@/models/utils/FormatUtils.js'
import { userApi } from '@/api/User/UserApi.js'
import { useLearningCenterStore } from '@/stores/learningCenterStore.js'
import { authChannel, AuthChannelMessage } from '@/composables/useAuthChannel.js'
import { systemEchoes } from '@/composables/echo/useSystemEchoes.js'
import { UserpilotService } from '@/models/utils/UserpilotUtils.js'
import { Tenants } from '@/models/tenants/Tenants.js'

/**
 * @typedef {function} authStore
 * @property {Object} state - authStore state
 * @property {ComagineJwtToken} state.auth - authStoreJwt object
 * @property {UserOrganizationData} state.userOrganizationData - Object containing user's organizations and currently active organization id
 * @property {boolean} state.unAuthenticated - Indicates a user has lost authentication
 * @property {number} state.lastUserDataRefresh
 * @property {Banner} state.banner Banner object to show messages
 * @property {Object} state.pointOfContact Point of contact user info
 * @function isAnyAdmin
 */

/**
 * @typedef {Object} TenantsRecord
 * @property {number} id Tenant ID
 * @property {string} name Tenant's name for application
 * @property {string} host Host for tenant (Ex: portal.comagine.org)
 * @property {string} email_from Address emails are sent from
 * @property {int} jwt_ttl JWT Timeout - Used to show session timeout warning dialog
 * @property {string} settings_json
 */

/**
 * @typedef {Object} UsersRecord
 * @property {number} id User ID
 * @property {string} username
 * @property {string} family_name
 * @property {string} given_name
 * @property {string} email
 * @property {string} phone_number
 * @property {string} tfa_method
 * @property {number} receive_sms
 * @property {number} is_admin
 * @property {number} show_experimental - Show experimental features
 * @property {number} is_developer - User is a developer
 * @property {number} tenant_id
 */

/**
 * @typedef {Object} ComaginePayload
 * @property {Organization[]} organizations Array of the user's Organizations
 * @property {TenantsRecord} tenant_info Information about the current tenant
 * @property {number} session_timeout Session timeout (JWT_TTL) for the user in minutes
 * @property {UsersRecord} user User record data
 */

/**
 * Authorization store used to track user login
 */
export const useAuthStore = defineStore('authStore', {
	state: () => ({
		/**
		 * Contains an object deserialized from jwt token.
		 * Access with getAuth
		 * @type {ComagineJwtToken}
		 */
		auth: useLocalStorage('authStoreJwt', null, {
			serializer: {
				read: ComagineJwtToken.deserialize,
				write: ComagineJwtToken.serialize
			}
		}),

		/**
		 * @type {UserOrganizationData}
		 */
		userOrganizationData: useLocalStorage('userOrganizationData', new UserOrganizationData([], 0), {
			serializer: {
				read: UserOrganizationData.deserialize,
				write: UserOrganizationData.serialize
			}
		}),
		unAuthenticated: false,
		lastUserDataRefresh: useLocalStorage('lastUserDataRefresh', 0),
		banner: new Banner(),
		pointOfContact: null
	}),
	getters: {
		/**
		 * Gets the Auth object
		 * @param {authStore.state} state
		 * @returns {ComagineJwtToken}
		 */
		getAuth(state) {
			return state.auth
		},
		/**
		 * Returns true if the user is currently logged in
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		isLoggedIn: (state) =>
		{
			return state.auth !== null && state.auth.isHeaderValid() && !state.auth.isExpired()
		},
		/**
		 * Returns the JWT as a raw string
		 * @param {authStore.state} state
		 * @returns {string}
		 */
		rawJwt: (state) =>
		{
			return state?.auth?.jwt
		},
		/**
		 * Returns true if the user is an admin of the portal
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		isPortalAdmin: (state) =>
		{
			return Format.bool(state.auth?.user()?.is_admin)
		},
		/**
		 * Returns true if a user can view experimental features
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		showExperimental: (state) =>
		{
			return Format.bool(state.auth?.user()?.show_experimental)
		},
		/**
		 * Returns true if a user can view developer only features
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		isDeveloper: (state) =>
		{
			return Format.bool(state.auth?.user()?.is_developer)
		},
		/**
		 * Returns true if the user is an admin of their active organization
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		isOrganizationAdmin: (state) =>
		{
			return state.userOrganizationData?.organizations?.filter((organization) => {
				return organization.admin_permissions.admin.enabled && state.userOrganizationData.activeOrganizationId === organization.id
			})?.length > 0
		},
		/**
		 * Returns an array of organization ids that the user is an admin of.
		 * @param {authStore.state} state
		 * @returns {Array<int>}
		 */
		getEditableOrgs: (state) =>
		{
			const ids = [];
			try
			{
				for (let i = 0; i < state.userOrganizationData?.organizations.length; i++)
				{
					const org = state.userOrganizationData.organizations[i];
					if (org.admin_permissions.admin.enabled) ids.push(org.id);
				}
			}
			catch (e)
			{
				if (Format.bool(state.auth?.user()?.is_developer))
				{
					console.error('authStore.getEditableOrgs: ', e);
				}
			}

			return ids;
		},
		/**
		 * Returns true if the user is an admin of any organizations they are associated with
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		isAnyOrganizationAdmin: (state) =>
		{
			return state.userOrganizationData?.organizations?.filter((organization) => {
				return organization.admin_permissions.admin.enabled
			})?.length > 0
		},
		/**
		 * Returns true if the user is a portal admin or organization admin
		 * @returns {(function(authStore.state): boolean)|(function(authStore.state): boolean)}
		 */
		isAnyAdmin()
		{
			return this.isPortalAdmin || this.isAnyOrganizationAdmin
		},
		/**
		 * Returns true if the user can upload files for their active organization
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		canUploadData:(state)=>
		{
			if (!state.userOrganizationData) return false
			const perms = state.userOrganizationData.orgPermissions()
			return perms.data_permissions.upload.enabled
		},
		/**
		 * Returns true if the user can download files for their active organization
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		canDownloadData:(state)=>
		{
			if (!state.userOrganizationData) return false
			const perms = state.userOrganizationData.orgPermissions()
			return perms.data_permissions.download.enabled
		},
		/**
		 * Returns true if the user can delete files for their active organization
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		canDeleteData:(state)=>
		{
			if (!state.userOrganizationData) return false
			const perms = state.userOrganizationData.orgPermissions()
			return perms.data_permissions.delete.enabled
		},
		/**
		 * Returns true if the user can approve documents
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		canApproveDocuments:(state)=>
		{
			if (!state.userOrganizationData) return false
			const perms = state.userOrganizationData.orgPermissions()
			return perms.document_permissions.approve.enabled
		},
		/**
		 * Returns true if the user can download documents
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		canDownloadDocuments:(state)=>
		{
			if (!state.userOrganizationData) return false
			const perms = state.userOrganizationData.orgPermissions()
			return perms.document_permissions.download.enabled
		},
		/**
		 * Returns true if the user can upload documents for their active organization
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		canUploadDocuments:(state)=>
		{
			if (!state.userOrganizationData) return false
			const perms = state.userOrganizationData.orgPermissions()
			return perms.document_permissions.upload.enabled
		},
		/**
		 * Returns true if the user can view all user files for their active organization
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		canViewAllOrgFiles:(state)=>
		{
			if (!state.userOrganizationData) return false
			const perms = state.userOrganizationData.orgPermissions()
			return perms.data_permissions.view_all_org_files.enabled
		},
		/**
		 * Returns true if the user can create new organizations
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		canCreateOrganization:(state)=>
		{
			if (!state.userOrganizationData) return false
			const perms = state.userOrganizationData.orgPermissions()
			return perms.admin_permissions.create_organization.enabled || state.isPortalAdmin
		},
		/**
		 * Returns true if the user can view/access support messages
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		canManageSupport:(state)=>
		{
			if (!state.userOrganizationData) return false
			const perms = state.userOrganizationData.orgPermissions()
			return perms.admin_permissions.manage_support.enabled || state.isPortalAdmin
		},
		/**
		 * Returns true if the user's current role the top admin role.
		 * @param {authStore.state} state
		 * @returns {boolean}
		 */
		isAdminRole:(state)=>
		{
			if (!state.userOrganizationData) return false
			const perms = state.userOrganizationData.orgPermissions()
			return perms.is_admin_role || state.isPortalAdmin
		},
		/**
		 * Returns the user's active organization id
		 * @param {authStore.state} state
		 * @returns {number}
		 */
		activeOrganizationId: (state) =>
		{
			return state.userOrganizationData?.activeOrganizationId ? state.userOrganizationData.activeOrganizationId : 0;
		},
		/**
		 * Returns the user's active organization id
		 * @param {authStore.state} state
		 * @returns {Organization}
		 */
		getActiveOrganization: (state) =>
		{
			return state.userOrganizationData?.organizations.find(org => org.id === state.userOrganizationData?.activeOrganizationId)
		},
		/**
		 * Returns the user's OrganizationData object
		 * @function
		 * @param {authStore.state} state
		 * @returns {UserOrganizationData}
		 */
		getUserOrganizationData: (state) =>
		{
			return state.userOrganizationData;
		},
		/**
		 * Returns the user's organizations or empty array
		 * @param {authStore.state} state
		 * @returns {Array<Organization>}
		 */
		userOrganizations: (state) =>
		{
			return state.userOrganizationData?.organizations ? state.userOrganizationData.organizations : [];
		},
		/**
		 * Returns the number of organizations a user has that are active
		 * @param {authStore.state} state
		 * @returns {number}
		 */
		userActiveOrganizationCount: (state) =>
		{
			return state.userOrganizationData?.organizations?.filter((organization) => {
				return organization.deleted_at === null
			})?.length
		},

		/**
		 * Gets the currently active org's point of contact user id.
		 * @param {authStore.state} state
		 * @returns {number} User ID or 0
		 */
		getPointOfContactId: (state) =>
		{
			const org_data = state.userOrganizationData?.organizations.find(org => org.id === state.userOrganizationData?.activeOrganizationId);
			if (org_data && org_data.point_of_contact_id > 0)
			{
				return org_data.point_of_contact_id;
			}
			return 0
		},

		/**
		 * Returns the current user's role for the active organization
		 * @param {authStore.state} state
		 * @returns {OrganizationRole|null}
		 */
		getUserRole: (state) =>
		{
			const perms = state.userOrganizationData.orgPermissions();
			if (perms) return perms;
			return null;
		},

		/**
		 * Returns the current user's role for the active organization
		 * @param {authStore.state} state
		 * @returns {function(*): OrganizationRole}
		 */
		getUserRoleForOrg: (state) =>
		{
			return (p_org_id) =>
			{
				const role = state.userOrganizationData.createOrganizationRole();
				const org = state.userOrganizationData.organizations.find(org => org.id === p_org_id)

				if (org)
				{
					role.id = org.user_role_id;
					role.role = org.user_role;
					role.is_admin_role = Format.bool(org.is_admin_role);
					role.hierarchy = parseFloat(org.hierarchy);

					role.admin_permissions = typeof org.admin_permissions === 'string' ? JSON.parse(org.admin_permissions) : org.admin_permissions;
					role.data_permissions = typeof org.data_permissions === 'string' ? JSON.parse(org.data_permissions) : org.data_permissions;
					role.document_permissions = typeof org.document_permissions === 'string' ? JSON.parse(org.document_permissions) : org.document_permissions;
					role.resource_permissions = typeof org.resource_permissions === 'string' ? JSON.parse(org.resource_permissions) : org.resource_permissions;
				}

				return role;
			}
		}
	},
	actions: {
		/**
		 * Clears this tab's session state. Does not talk to the backend, broadcast, or navigate -
		 * callers own those. Split out of logout() so a logout triggered by another tab can reuse it.
		 */
		clearLocalState()
		{
			useSignupStore().logout()
			//This caused users to have to enter their tfa token after every logout useTwoFactorVerificationStore().logout()
			useOrganizationStore().logout()
			useLearningCenterStore().logout()

			this.auth = null
			this.userOrganizationData = null
			this.payload = null
			this.unAuthenticated = false
			this.lastUserDataRefresh = 0
			this.pointOfContact = null

			systemEchoes.systemDisconnect();

			UserpilotService.clean();
		},

		/**
		 * Logs out a user. Appears to get called twice after clicking logging out b/c the Login page calls it when mounting.
		 *
		 * The isLoggedIn guard is load-bearing: the Login page calls logout(false) on mount, so without it
		 * every tab landing on the login page would broadcast another logout and the other tabs would
		 * redirect back to the login page, broadcasting again - an endless cross-tab reload loop.
		 * @param reroute
		 */
		logout(reroute = true)
		{
			if (this.isLoggedIn)
			{
				userApi.logout()
				authChannel.post({ type: AuthChannelMessage.Logout })
			}

			this.clearLocalState()

			if (reroute)
			{
				router.push({ name: 'auth.login' })
			}
		},

		/**
		 * Handles a logout that happened in another tab. Only called by useAuthSync, which owns the
		 * decision of whether this tab should react. The backend has already invalidated the JWT and
		 * this tab's token is already gone from localStorage, so only local state needs clearing -
		 * re-calling userApi.logout() or re-broadcasting would just loop back around the tabs.
		 *
		 * Navigates with a full page load rather than router.push so nothing survives from the
		 * logged-in session: stale Pinia state, session-timeout timers, open modals, in-flight uploads.
		 */
		handleRemoteLogout()
		{
			this.clearLocalState()

			window.location.assign(router.resolve({ name: 'auth.login', query: { signedOut: 1 } }).href)
		},

		handleLoginResult(result, email, password)
		{
			const tfaStore = useTwoFactorVerificationStore()

			// Captured before this.auth is replaced. handleLoginResult also runs on the periodic
			// refreshUserData() and after a profile update, where this tab was already signed in and no
			// other tab needs telling.
			const wasLoggedIn = this.isLoggedIn

			switch (result?.data?.status ?? LoginResultStatus.Unknown)
			{
				case LoginResultStatus.Success:
					if (result?.data?.authorization?.type !== 'bearer' || !result?.data?.authorization?.token)
					{
						throw 'Unknown error - Login successful but JWT token not found.'
					}
					this.auth = new ComagineJwtToken(result?.data)
					this.userOrganizationData = new UserOrganizationData(this.auth?.payload?.comagine?.organizations ?? [], 0)

					//Sort data for display in settings
					this.userOrganizationData?.organizations?.sort((a, b) => a.organization_name.localeCompare(b.organization_name))

					// If the user only belongs to one org, set it as active
					if (!this.userOrganizationData?.activeOrganizationId && this.userActiveOrganizationCount === 1)
					{
						this.userOrganizationData.activeOrganizationId = this.userOrganizationData?.organizations[0].id
					}

					if (!wasLoggedIn)
					{
						// nextTick, not inline: auth and userOrganizationData are useLocalStorage refs whose
						// writes flush on the pre-flush queue, so posting synchronously could reach a tab that
						// then reloads and reads localStorage before this tab has written it.
						nextTick(() => authChannel.post({ type: AuthChannelMessage.Login }))
					}

					//Reconnect the user if they were logged out or lost their marbles.
					//	Note: Authentication is called when channel is subscribed to. To reduce calls, config is set to batchAuth: true
					systemEchoes.systemConnect();

					return true
				case LoginResultStatus.TfaRequired:
					if (!result?.data?.authorization?.tfa_token)
					{
						this.banner.error('Unknown error - TFA required but verification token not present.')
						throw 'Unknown error - TFA required but verification token not present.'
					}
					tfaStore.setVerificationToken(email, password, result?.data?.authorization?.tfa_token)
					router.push({ name: 'auth.verify-tfa' })
					return false
				case LoginResultStatus.Unknown:
				default:
					//this.banner.error('Unknown auth login result.')
					console.warn("Unknown Result", result);
					throw 'Unknown auth login result.'
			}
		},

		handleUpdateResult(result)
		{
			switch (result?.data?.status ?? LoginResultStatus.Unknown)
			{
				case LoginResultStatus.Success:
					if (result?.data?.authorization?.type !== 'bearer' || !result?.data?.authorization?.token)
					{
						throw 'Unknown error - Login successful but JWT token not found.'
					}
					this.auth = new ComagineJwtToken(result?.data)

					return true

				case LoginResultStatus.Unknown:
				default:

					this.banner.error('Unknown authentication result.')
					throw 'Unknown auth login result.'
			}
		},

		/**
		 * Checks if response from a request to see if it is due to loss of authentication.
		 * Called by ApiGateway.hasError (this causes it to get called multiple times b/c of reactivity), so console writes will get duplicated;
		 * @param p_response {Object} Response from a request containing error information
		 */
		handleResponseAuthError(p_response)
		{
			try
			{
				if (!p_response.error != null && p_response.error.response != null)
				{
					const error = p_response.error.response;
					//Removed check for: && error.statusText && (error.statusText.toLowerCase().indexOf('unauth') !== -1 || error.statusText.toLowerCase().indexOf('forbidden') !== -1)
					//	In the Comagine dev env, statusText was returning blank and could not be trusted
					if (error.status && (error.status === HttpStatusCode.Unauthorized ) )//Forbidden is different to unauthorized error.status === HttpStatusCode.Forbidden
					{
						this.handleUnAuthenticated()
					}
					else
					{
						//Don't show banners or toasts in here, the input fields call hasError in a computed property that'll
						//	show them till a recursion error shows.

						//this.banner.error(p_response.errorMessages())
						//Uncomment for debugging
						//console.error("handleResponseAuthError", error)
					}
				}
			}
			catch (e)
			{
				console.error(e)
			}
		},

		/**
		 * Performs the actions required when an unauthorized request is found. Alert modal is shown based on this.unAuthenticated, see Layouts.
		 */
		handleUnAuthenticated()
		{
			this.unAuthenticated = true;
		},

		/**
		 * Set the user's active organization id
		 * @param p_id
		 */
		setActiveOrganizationId(p_id){
			if (this.userOrganizationData)
			{
				this.userOrganizationData.activeOrganizationId = p_id;

				systemEchoes.systemConnect();
			}
		},

		/**
		 * Set the user's userOrganizationData object
		 * @param {UserOrganizationData} p_data
		 */
		setUserOrganizationData(p_data){
			this.userOrganizationData = p_data
			this.loadPointOfContact()
		},

		/**
		 * Set the user's organizations in userOrganizationData
		 * @param {Array<Organization>} p_organizations
		 */
		setUserOrganizations(p_organizations){
			this.userOrganizationData.organizations = p_organizations
			this.loadPointOfContact()
		},

		/**
		 * Gets the currently active org's point of contact
		 */
		loadPointOfContact()
		{
			try
			{
				const org_data = this.userOrganizationData?.organizations.find(org => org.id === this.userOrganizationData?.activeOrganizationId);
				if (org_data && org_data.point_of_contact)
				{
					this.pointOfContact = org_data.point_of_contact;
				}
				else
				{
					this.pointOfContact = null;
				}
			}
			catch (e)
			{
				console.error('Failed to load Point of Contact')
			}
		},

		setJwtVersionCode(jwtVersionCode) {
			this.auth.user().jwt_version_code = jwtVersionCode
		},

		/**
		 * Identify a user with the Userpilot api.
		 */
		userpilotIdentify()
		{
			//Safe to call from anywhere: auth is null once clearLocalState() has run
			if (!this.isLoggedIn) return;

			const tenant = Tenants.getTenant()
			const user = this.auth.user()
			UserpilotService.identify(String(user.id), {
				//name: user.full_name,
				//email: user.email,
				//created_at: user.created_at,   // ISO8601
				company: {
					id: String(user.tenant_id),
					name: tenant.name
				}
			})
		}
	}
})

/**
 * Handles a ComagineJwtToken
 * @property {string} jwt Jwt string
 * @property {Object} header Header in jwt
 * @property {ComaginePayload} payload Payload in jwt
 * @property {function} user
 * @property {function} tenantInfo Returns the tenant info from the authorization request
 */
export class ComagineJwtToken
{
	/**
	 * Creates a new ComagineJwtToken instance.
	 * @param {object|null} resp - The object containing JWT token, payload, etc., or null
	 */
	constructor(resp)
	{
		this.jwt = null
		this.header = null
		this.payload = null

		if (!resp) return;

		if (resp['authorization'])
		{
			this.loadServiceResponse(resp)
		}
		else
		{
			this.jwt = resp['jwt'];

			if (this.jwt !== null)
			{
				const { header, payload } = useJwt(this.jwt)
				this.header = header.value
				this.payload = payload.value

				if (this.payload)
				{
					if (resp && resp['payload'] && resp['payload']['comagine'])
					{
						this.payload['comagine'] = resp['payload']['comagine'];
					}
					else
					{
						this.payload['comagine'] = (resp?.user_data) ? resp?.user_data['comagine'] : null;
					}
				}
			}

		}
	}

	/**
	 * Loads the response from the app service
	 * @param {{"user": Object, "tfaToken": string, "authorization": Object, "user_data": Object }} p_resp
	 */
	loadServiceResponse(p_resp)
	{

		if (p_resp['authorization'] && p_resp['authorization']['token'])
		{
			this.jwt = p_resp.authorization.token
		}
		if (this.jwt !== null)
		{
			const { header, payload } = useJwt(this.jwt)
			this.header = header.value
			this.payload = payload.value

			if (this.payload)
			{
				this.payload['comagine'] = (p_resp?.user_data) ? p_resp?.user_data['comagine'] : null;
			}
		}
	}

	/**
	 * Deserializes a raw string into a ComagineJwtToken instance.
	 * @param {string} raw - The raw JWT token string to deserialize
	 * @returns {ComagineJwtToken|null} A new ComagineJwtToken instance, or null if the input is empty
	 */
	static deserialize(raw)
	{
		if (raw === '' || raw.trim().length <= 0)
		{
			return null
		}
		return new ComagineJwtToken(JSON.parse(raw))
	}

	/**
	 * Serializes a ComagineJwtToken instance back to its raw JWT string.
	 * @param {ComagineJwtToken|null} value - The token instance to serialize
	 * @returns {string} The raw JWT string, or an empty string if value is null/undefined
	 */
	static serialize(value)
	{
		return JSON.stringify(value)
	}

	isHeaderValid()
	{
		return this.header?.typ === 'JWT'
	}

	isExpired()
	{
		return (this.payload?.exp ?? 0) < (Date.now() / 1000.0)

	}

	/**
	 * Extracts the user information from the JWT payload.
	 * @returns {UsersRecord|undefined} The user object from the token's payload, or undefined if not present
	 */
	user()
	{
		return this.payload?.comagine?.user
	}

	/**
	 * Returns tenant record
	 * @returns {TenantsRecord}
	 */
	tenantInfo()
	{
		return this.payload?.comagine?.tenant_info
	}

	/**
	 * Returns tenant record
	 * @returns {
	 * {
	 * 		"session_timeout_hours": { "value": number, "minimum": number, "description": string },
	 * 		"jwt_ttl": { "value": number, "minimum": number, "description": string },
	 * 		"support_message_subjects": { "value": string, "pretty_name": string, "description": string },
	 * 		"documents_archive_after_days": { "value": number, "description": string },
	 * 		"data_submissons_archive_after_days": { "value": number, "description": string },
	 * 		"modules": { "value": Array<Object>, "description": string },
	 * 		"scheduled_reports": { "value": {"data_submissoins": { "enabled": boolean, 'name': string }}, "description": string}
	 * }
	 * |null}
	 */
	tenantSettings()
	{
		try
		{
			return JSON.parse(this.payload?.comagine?.tenant_info?.settings_json)
		}
		catch (e)
		{
			console.error('Failed to parse tenant settings', e)
		}
		return null
	}

	/**
	 * Returns user session timeout setting (in minutes)
	 * @returns {number}
	 */
	sessionTimeout()
	{
		//+ 2 to offset buffer used to avoid caching messing everything up
		const timeout = Number(JSON.parse(this.payload?.comagine?.tenant_info?.settings_json)?.jwt_ttl?.value) + 2
		return typeof timeout === 'number' ? timeout : 60
	}
}

/**
 * @typedef {Object} Organization
 * @property {number} id - Organization ID
 * @property {number} user_organization_id
 * @property {number} user_id
 * @property {number} fqhc
 * @property {string} user_role Role name
 * @property {string|AdminPermissions} admin_permissions Admin Permissions
 * @property {string|DataPermissions} data_permissions Data Permissions
 * @property {string|DocumentPermissions} document_permissions Document Permissions
 * @property {string|ResourcePermissions} resource_permissions Resource Permissions
 * @property {string} role Name of the role
 * @property {boolean} is_admin_role True if top dog admin role
 * @property {string} hierarchy Role (comes in as string from service)
 * @property {number} user_role_id Organization_roles.id
 * @property {string} user_title
 * @property {string} point_of_contact_id
 * @property {string} user_organization_deleted_at
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string} deleted_at
 * @property {string} ccn
 * @property {string} organization_name
 * @property {string} organization_name_dba
 * @property {string} parent_organization
 * @property {string} facility_category
 * @property {string} facility_type
 * @property {string} region
 * @property {string} street
 * @property {string} city
 * @property {string} state
 * @property {string} zip
 * @property {string} phone
 * @property {string} tin
 * @property {string} npi
 * @property {string} date_opened
 * @property {string} date_closed
 */

/**
 * @typedef {Object} OrganizationRole
 * @property {number} id Organization Role ID
 * @property {string} role Name of the role
 * @property {number} hierarchy Role's hierarchy
 * @property {boolean} is_admin_role True if this is the top admin role
 * @property {AdminPermissions} admin_permissions Admin Permissions
 * @property {DataPermissions} data_permissions Data Permissions
 * @property {DocumentPermissions} document_permissions Document Permissions
 * @property {ResourcePermissions} resource_permissions Resource Permissions
 */

/**
 * @typedef {Object} RolePermission
 * @property {string} title Permission title
 * @property {string} description Description of the permission
 * @property {boolean} enabled Permission is active for this role
 * @property {boolean} editable Permission can be modified
 */

/**
 * @typedef {Object} AdminPermissions
 * @property {RolePermission} admin Admin permission
 * @property {RolePermission} manage_support View support
 * @property {RolePermission} point_of_contact User can be set as point of contact
 * @property {RolePermission} create_organization User can crate organizations
 */

/**
 * @typedef {Object} DataPermissions
 * @property {RolePermission} upload Upload
 * @property {RolePermission} view_all_org_files User can view all user files for their active organization
 * @property {RolePermission} download Download data submissions
 * @property {RolePermission} delete Delete data submissions
 */

/**
 * @typedef {Object} DocumentPermissions
 * @property {RolePermission} upload Upload Documents
 * @property {RolePermission} approve Approve submitted documents
 * @property {RolePermission} download Download documents
 */

/**
 * @typedef {Object} ResourcePermissions
 * @property {RolePermission} download_documents Download documents from Resources page
 */

/**
 * Class that contains a user's assigned organizations and their currently active organization.
 * @property {Array} organizations
 * @property {number} activeOrganizationId
 */
export class UserOrganizationData
{
	/**
	 * Constructor to create a UserOrganizationData object
	 * @param {Array<Organization>} organizations Path to api
	 * @param {number} activeOrganizationId Number of items to show per page
	 */
	constructor(organizations, activeOrganizationId)
	{
		for (let i = 0; i < organizations.length; i++)
		{
			this.deserializePermission(organizations[i], 'admin_permissions')
			this.deserializePermission(organizations[i], 'data_permissions')
			this.deserializePermission(organizations[i], 'document_permissions')
			this.deserializePermission(organizations[i], 'resource_permissions')
		}

		this.organizations = organizations
		this.activeOrganizationId = activeOrganizationId
	}

	static deserialize(raw)
	{
		if (raw === '' || raw.trim().length <= 0)
		{
			return null
		}
		const user_org_data = JSON.parse(raw)

		return new UserOrganizationData(user_org_data.organizations, user_org_data.activeOrganizationId)
	}

	static serialize(value)
	{
		return JSON.stringify(value)
	}

	/**
	 * Deserializes the permissions objects if they're strings
	 * @param p_organization Organization
	 * @param p_permission Permission
	 */
	deserializePermission(p_organization, p_permission)
	{
		try
		{
			if (typeof p_organization[p_permission] === 'string')
			{
				p_organization[p_permission] = JSON.parse(p_organization[p_permission]);
			}
		}
		catch (e)
		{
			console.error(e);
		}
	}

	/**
	 * Sort organizations by name. Pass true to move the currently active organization to the top of the list.
	 * @param p_active_to_top_eh Pass true to move currently active organization to the top of the list
	 */
	sortOrganizations(p_active_to_top_eh)
	{
		if (!this.organizations) return;
		try
		{
			// Move the active org to the top
			if (p_active_to_top_eh)
			{
				const activeOrg = this.organizations.find(org => org.id === this.activeOrganizationId)
				const otherOrgs = this.organizations.filter(org => org.id !== this.activeOrganizationId).sort(function(a, b)
				{
					return a.organization_name.localeCompare(b.organization_name)
				})

				this.organizations = [activeOrg, ...otherOrgs]
			}
			else
			{
				//Sort data for display in settings
				this.organizations?.sort((a, b) => a.organization_name.localeCompare(b.organization_name))
			}
		}
		catch(e)
		{
			console.error(e, 'Failed to sort organizations', 'active_to_top:', p_active_to_top_eh);
		}
	}

	/**
	 * Create Role Permission Object
	 * @param p_title
	 * @returns {RolePermission}
	 */
	createRolePermission(p_title)
	{
		return {
			"title": p_title,
			"description": "",
			"enabled": false,
			"editable": false
		}
	}

	/**
	 * Create base Admin permission object
	 * @returns {AdminPermissions}
	 */
	createAdminPermission()
	{
		return {
			"admin": this.createRolePermission("Admin"),
			"manage_support": this.createRolePermission("Manage Support"),
			"point_of_contact": this.createRolePermission("Point of Contact"),
			"create_organization": this.createRolePermission("Create Organization")
		}
	}

	/**
	 * Create base Data Submission permission object
	 * @returns {DataPermissions}
	 */
	createDataPermission()
	{
		return {
			"upload": this.createRolePermission("Upload"),
			"view_all_org_files": this.createRolePermission("View All Organization Files"),
			"download": this.createRolePermission("Download"),
			"delete": this.createRolePermission("Delete")
		};
	}

	/**
	 * Create base Documents permission object
	 * @returns {DocumentPermissions}
	 */
	createDocumentPermission()
	{
		return {
			"upload": this.createRolePermission("Upload"),
			"approve": this.createRolePermission("Approve Documents"),
			"download": this.createRolePermission("Download")
		}
	}

	/**
	 * Create base Resources permission object
	 * @returns {ResourcePermissions}
	 */
	createResourcePermission()
	{
		return {
			"download_documents": this.createRolePermission("Download Documents")
		}
	}

	/**
	 * Creates an empty Role object
	 * @returns {OrganizationRole}
	 */
	createOrganizationRole()
	{
		/*
			role = name of the current role
			id = id of the role
			hierarchy = hierarchy of the role
			is_admin_role = true if this is the top admin role

			admin_permissions:
				admin: Admin role
				create_organization: User can create organizations
				point_of_contact: User can be set as a point of contact
				manage_support: User can view/manage support messages

			data_permissions:
				upload: User can upload files
				view_all_org_files: User can view all user files for their active organization

			document_permissions:
				upload: User can upload documents
				approve: User can approve documents to publish them
				download: User can download documents (generally, does not include users assigned as user to report)

			resource_permissions
				download_documents: User can download files shown on the resource page uploaded via documents
		 */
		return {
			role: "",
			id: 0,
			is_admin_role: false,
			hierarchy: 0,
			admin_permissions: this.createAdminPermission(),
			data_permissions: this.createDataPermission(),
			document_permissions: this.createDocumentPermission(),
			resource_permissions: this.createResourcePermission()
		}
	}

	/**
	 * Gets the permissions objects for the active organization
	 * @returns {OrganizationRole}
	 */
	orgPermissions()
	{
		const perms = this.createOrganizationRole();

		try
		{
			if (this.activeOrganizationId && this.activeOrganizationId > 0)
			{
				const activeOrg = this.organizations.find(org => org.id === this.activeOrganizationId)
				if (activeOrg)
				{
					//See UserOrganizationController.getUserOrganizations for properties
					perms.is_admin_role = Format.bool(activeOrg.is_admin_role)
					perms.hierarchy = parseFloat(activeOrg.hierarchy)
					perms.role = activeOrg.user_role
					perms.id = activeOrg.user_role_id

					perms.admin_permissions = this.getPermission(perms.admin_permissions, activeOrg.admin_permissions)
					perms.data_permissions = this.getPermission(perms.data_permissions, activeOrg.data_permissions)
					perms.document_permissions = this.getPermission(perms.document_permissions, activeOrg.document_permissions)
					perms.resource_permissions = this.getPermission(perms.resource_permissions, activeOrg.resource_permissions)
				}
			}
		}
		catch(e)
		{
			console.error(e)
		}

		return perms;
	}

	/**
	 * Gets the permission object, converts json string to object if necessary. Returns default with everything set false to prevent errors if there's an issue with the object
	 * @param p_default Default permissions object with everything disabled
	 * @param p_source Permission in the active org
	 * @returns {object}
	 */
	getPermission(p_default, p_source)
	{
		try
		{
			if (typeof p_source === 'object') return p_source;
			if (typeof p_source === 'string')
			{
				return JSON.parse(p_source)
			}

		}
		catch(e)
		{
			console.error('Failed to parse permission', 'p_source', p_source)
		}
		return p_default
	}
}
